/**
 * OnboardingFlowProvider
 * Context provider for the client onboarding flow.
 * Acts as a thin wrapper around Redux state to provide context to child components.
 */

import { determineCompanyIcpType } from '@hellodarwin/app-core/lib/components/marketing-controlled-buttons/rules/check-company-icp';
import { usePremiumAccess } from '@hellodarwin/app-core/lib/providers/premium-access-provider';
import { QueryFundingExplorerProps } from '@hellodarwin/core/lib/features/entities';
import {
  CompanyIcpType,
  Contact,
  isNPO,
} from '@hellodarwin/core/lib/features/entities/core-entities';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import { useForm } from 'antd/es/form/Form';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useAppDispatch,
  useAppSelector,
  useAppUnwrap,
} from '../../../app/app-hooks';
import { useClientAnalytics } from '../../../features/analytics/use-client-analytics';
import {
  fetchFundingExplorerTotalAmount,
  fetchIndustriesICNPO,
  fetchLegalStructures,
  fetchNPOBeneficiaries,
  fetchNPOStreams,
  selectLegalStructuresMap,
} from '../../../features/api/slices/grants-slice';
import {
  acceptTermsAndConditions,
  fetchCompany,
  onboard,
  selectContact,
} from '../../../features/api/slices/profile-slice';
import {
  fetchIndustries,
  selectIndustrySectorsMap,
} from '../../../features/api/slices/tags-slice';
import {
  useClientApi,
  useNewClientApi,
} from '../../../features/api/use-client-api';
import { useUser } from '../../../features/providers/user-provider';
import { LocalStorageKeys } from '../../../local-storage';
import {
  checkEligibility,
  clearError,
  markStepCompleted,
  saveStepData,
  selectCompletedSteps,
  selectCurrentStep,
  selectIsExpertEligible,
  selectOnboardingData,
  selectSkipAvailable,
  setCurrentStep,
  updateOnboardingData,
} from '../api/onboarding-slice';
import useStepsContent, { StepFormItem } from '../data/steps-content';
import stepsSequence from '../data/steps-sequence';
import {
  defaultContextValue,
  OnboardingContextType,
  OnboardingData,
  OnboardingStep,
} from '../types/onboarding';
import { buildAnalyticsData } from '../utils/build-analytics-data';
import getNextStep from '../utils/navigation/getNextStep';
import getPrevStep from '../utils/navigation/getPrevStep';
import validateStep from '../utils/navigation/validateStep';

/**
 * OnboardingContext - React context for the onboarding flow
 * Provides state and functions to control the onboarding process
 */
export const OnboardingContext =
  createContext<OnboardingContextType>(defaultContextValue);
OnboardingContext.displayName = 'OnboardingContext';

/**
 * Provider component for onboarding flow
 * Uses Redux for state management and exposes helpful methods via context
 */
export const OnboardingFlowProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // External hooks
  const clientApi = useClientApi();
  const clientApiV2 = useNewClientApi();
  const { t } = useTranslation();
  const { t: t2 } = useTranslations();
  const dispatch = useAppDispatch();
  const unwrap = useAppUnwrap();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasInitializedFromUrl, setHasInitializedFromUrl] = useState(false);
  const analytics = useClientAnalytics();
  const { selectedLocale } = useLocale();
  // Use optional chaining to safely access setOnboarding method
  const { setOnboarding, onboarding, profile } = useUser() || {};

  // Local state for form instance
  const [onboardingForm] = useForm();

  // Redux selectors for onboarding state
  const currentStep = useAppSelector(selectCurrentStep);
  const stepData = useAppSelector(selectOnboardingData);
  const completedSteps = useAppSelector(selectCompletedSteps);
  const skipAvailable = useAppSelector(selectSkipAvailable);
  const isExpertEligible = useAppSelector(selectIsExpertEligible);

  // Redux selectors for analytics data
  const companyLegalStructuresById = useAppSelector(selectLegalStructuresMap);
  const companyIndustrySectorsById = useAppSelector(selectIndustrySectorsMap);

  const contact = useAppSelector(selectContact);

  // Calculate flow steps based on user intent.
  // Skip the payment step if the user already has an active subscription
  // (e.g. they paid via a promo checkout link before completing onboarding).
  const flowSteps = useMemo(() => {
    const baseSteps = stepsSequence[stepData.type];
    if (
      profile?.access_type === 'premium' ||
      profile?.access_type === 'expert' ||
      profile?.access_type === 'custom'
    ) {
      return baseSteps.filter((step) => step !== 'payment');
    }
    return baseSteps;
  }, [stepData.type, profile]);

  const handleOnboardingStartedAnalytics = (flowStartingPoint: string) => {
    if (!onboarding) {
      return;
    }
    if (localStorage.getItem('onboarding_started') === '1') {
      return;
    }
    localStorage.setItem('onboarding_started', '1');
    analytics.onboardingStarted({ ...stepData }, flowStartingPoint);
  };

  useEffect(() => {
    if (hasInitializedFromUrl || !onboarding) return;

    navigate('/');

    const searchParams = new URLSearchParams(location.search);
    const stepFromUrl = searchParams.get(
      'onboardingStep',
    ) as OnboardingStep | null;

    if (stepFromUrl && flowSteps.includes(stepFromUrl)) {
      dispatch(setCurrentStep(stepFromUrl));
      setHasInitializedFromUrl(true);
    } else {
      const defaultStep = 'welcome';
      dispatch(setCurrentStep(defaultStep));
      handleOnboardingStartedAnalytics(defaultStep);
      setHasInitializedFromUrl(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboarding, flowSteps]);

  const previousSteps = useMemo(() => {
    return flowSteps.slice(0, flowSteps.indexOf(currentStep));
  }, [flowSteps, currentStep]);
  // Get step content data
  const stepsContent = useStepsContent(flowSteps);

  // Get current step content
  const currentStepContent = useMemo(() => {
    return stepsContent[currentStep];
  }, [currentStep, stepsContent]);

  const completeOnboarding = useCallback(async (): Promise<boolean> => {
    // Mark onboarding as complete in the backend
    if (stepData.formData?.company_name && stepData.formData?.annual_revenue) {
      try {
        await unwrap(
          onboard({
            api: clientApi,
            data: {
              company_name: stepData.formData.company_name,
              annual_revenue: stepData.formData.annual_revenue,
              address: stepData.formData.address,
              city: stepData.formData.city,
              postal_code: stepData.formData.postal_code,
              province: stepData.formData.province,
              country: stepData.formData.country,
              company_size: stepData.formData.company_size,
            },
          }),
        );
      } catch (_) {
        // The thunk already surfaced the API error; keep the user in onboarding.
        return false;
      }
    }
    return true;
  }, [stepData.formData, unwrap, clientApi]);

  // Navigation to dashboard
  const goToDashboard = useCallback(
    async (result: string) => {
      if (!(await completeOnboarding())) return;

      const searchParams = new URLSearchParams(location.search);
      searchParams.delete('onboardingStep');
      navigate('/dashboard');
      localStorage.setItem(LocalStorageKeys.ONBOARDING, 'none');
      setOnboarding('none');
      const analyticsData = buildAnalyticsData(
        stepData,
        companyLegalStructuresById,
        companyIndustrySectorsById,
      );
      analytics.onboardingCompleted(analyticsData, currentStep, result);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      stepData,
      companyLegalStructuresById,
      companyIndustrySectorsById,
      completeOnboarding,
      dispatch,
      clientApi,
    ],
  );

  const goToFundingExplorer = useCallback(async () => {
    if (!(await completeOnboarding())) return;

    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('onboardingStep');
    navigate('/funding-explorer');
    localStorage.setItem(LocalStorageKeys.ONBOARDING, 'none');
    setOnboarding('none');
    const analyticsData = buildAnalyticsData(
      stepData,
      companyLegalStructuresById,
      companyIndustrySectorsById,
    );
    analytics.onboardingCompleted(analyticsData, currentStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stepData,
    companyLegalStructuresById,
    companyIndustrySectorsById,
    completeOnboarding,
    dispatch,
    clientApi,
  ]);

  const goToLink = useCallback(
    async (link: string) => {
      if (!(await completeOnboarding())) return;

      const searchParams = new URLSearchParams(location.search);
      searchParams.delete('onboardingStep');
      navigate(link);
      localStorage.setItem(LocalStorageKeys.ONBOARDING, 'none');
      setOnboarding('none');
      const analyticsData = buildAnalyticsData(
        stepData,
        companyLegalStructuresById,
        companyIndustrySectorsById,
      );
      analytics.onboardingCompleted(analyticsData, currentStep);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      stepData,
      companyLegalStructuresById,
      companyIndustrySectorsById,
      completeOnboarding,
      dispatch,
      clientApi,
    ],
  );

  const { handleCheckout } = usePremiumAccess();

  const goToCheckout = useCallback(async () => {
    if (!(await completeOnboarding())) return;

    handleCheckout();
    localStorage.setItem(LocalStorageKeys.ONBOARDING, 'none');
    setOnboarding('none');
  }, [completeOnboarding, handleCheckout, setOnboarding]);

  // Navigation to next step
  const goToNextStep = useCallback(() => {
    const nextStepValue = getNextStep(currentStep, flowSteps);
    if (nextStepValue) {
      analytics.onboardingStepCompleted({ step_key: nextStepValue }, stepData);
      dispatch(setCurrentStep(nextStepValue));
    } else {
      goToDashboard('completed');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, dispatch, flowSteps, analytics, stepData]);

  // Navigation to previous step
  const goToPreviousStep = useCallback(() => {
    const prevStepValue = getPrevStep(currentStep, flowSteps);
    if (prevStepValue) {
      dispatch(setCurrentStep(prevStepValue));
    }
  }, [currentStep, dispatch, flowSteps]);

  const setIsExpertEligible = useCallback(
    (isExpertEligible: boolean) => {
      dispatch(
        updateOnboardingData({
          is_expert_consultation_eligible: !stepData.disable_expert_consultation
            ? isExpertEligible
            : false,
        }),
      );
    },
    [dispatch, stepData.disable_expert_consultation],
  );

  const validateIfExpertEligible = useCallback(() => {
    if (stepData.disable_expert_consultation) return;
    const isFormAnnualRevenueEligible =
      determineCompanyIcpType(
        selectedLocale,
        stepData.formData?.annual_revenue,
        stepData.formData?.province,
        stepData.formData?.industry_sector,
        stepData.formData?.legal_structure,
      ) === CompanyIcpType.Custom;
    const isCompanyAnnualRevenueEligible =
      determineCompanyIcpType(
        selectedLocale,
        stepData.company?.annual_revenue,
        stepData.company?.province,
        stepData.company?.industry_sector,
        stepData.company?.legal_structure,
      ) === CompanyIcpType.Custom;

    if (isCompanyAnnualRevenueEligible || isFormAnnualRevenueEligible) {
      if (!isExpertEligible) setIsExpertEligible(true);
    } else {
      if (isExpertEligible) setIsExpertEligible(false);
    }
  }, [isExpertEligible, setIsExpertEligible, stepData, selectedLocale]);

  useEffect(() => {
    validateIfExpertEligible();
  }, [validateIfExpertEligible]);

  // Update step data
  const setStepData = useCallback(
    (data: Partial<OnboardingData>) => {
      dispatch(updateOnboardingData(data));
    },
    [dispatch],
  );

  useEffect(() => {
    if (!currentStep || !hasInitializedFromUrl) return;

    const searchParams = new URLSearchParams(location.search);
    const currentParam = searchParams.get('onboardingStep');

    if (currentParam !== currentStep) {
      searchParams.set('onboardingStep', currentStep);
      const newUrl = `${location.pathname}?${searchParams.toString()}`;
      navigate(newUrl, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, navigate, location.pathname, location.search]);

  const fundingExplorerProps = useMemo(() => {
    const formItemsNames = [...previousSteps, currentStep]
      .reduce((acc, step) => {
        if (!stepsContent[step]?.formItems) return acc;
        const formItems = stepsContent[step]?.formItems;
        acc.push(...formItems);
        return acc;
      }, [] as StepFormItem[])
      .map((item) => item.name);
    const props: QueryFundingExplorerProps = {
      locale: selectedLocale,
      page: 1,
      limit: 3,
      industry: formItemsNames.includes('industry_sector')
        ? stepData.formData.industry_sector
        : undefined,
      status: ['open'],
      region: formItemsNames.includes('complete')
        ? [stepData.formData.province ? stepData.formData.province : '']
        : undefined,
      annualRevenue: formItemsNames.includes('annual_revenue')
        ? stepData.formData.annual_revenue
        : undefined,
      companySize: formItemsNames.includes('company_size')
        ? stepData.formData.company_size
        : undefined,
      goalsTags: formItemsNames.includes('project_goals')
        ? stepData.formData.project_goals
        : undefined,
    };
    return props;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousSteps, stepData]);

  // Submit step data and move to next step
  const handleSubmitStep = useCallback(async () => {
    const step = currentStep;
    // Clear any previous errors
    dispatch(clearError());

    // Validate step data
    const validation = validateStep(step, stepData, t, t2);
    if (!validation.isValid) {
      // If validation fails, return early without proceeding
      return;
    }

    try {
      // Save data to API
      await unwrap(
        saveStepData({
          api: clientApi,
          data: stepData,
        }),
      );

      // Check expert consultation eligibility when annual revenue is updated
      if (
        stepData.formData?.annual_revenue &&
        stepData.formData?.legal_structure &&
        step === 'company-info-1'
      ) {
        dispatch(
          checkEligibility({
            selectedLocale: selectedLocale,
            annualRevenue: stepData.formData.annual_revenue,
            province: stepData.formData.province,
            industries: stepData.formData.industry_sector,
            legalStructure: stepData.formData.legal_structure,
          }) as any,
        );
      }

      if (isNPO(stepData.formData?.legal_structure ?? '')) {
        setStepData({
          type: 'npo',
        });
      }

      // Mark step as completed
      dispatch(markStepCompleted(step));

      // Move to next step
      goToNextStep();
    } catch (error) {
      // Handle errors silently for now
      console.error('Error submitting step data:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepData, dispatch, clientApi, goToNextStep, t, currentStep, analytics]);

  // Skip onboarding
  const skipOnboarding = useCallback(async () => {
    if (skipAvailable) {
      await goToDashboard('skipped');
    }
  }, [skipAvailable, goToDashboard]);

  // Add helper for step validation
  const validateCurrentStep = useCallback(async () => {
    // Form validation
    const isValid = !!(await onboardingForm
      ?.validateFields({ validateOnly: true })
      .then(() => true)
      .catch(() => false));
    return isValid;
  }, [onboardingForm]);

  const goToStep = useCallback(
    (step: OnboardingStep) => {
      dispatch(setCurrentStep(step));
    },
    [dispatch],
  );

  const openExpertLink = useCallback(() => {
    window.open(t('onboardingClient|expertLink'), '_blank');
  }, [t]);

  const disableExpertEligible = useCallback(() => {
    dispatch(
      updateOnboardingData({
        disable_expert_consultation: true,
        is_expert_consultation_eligible: false,
      }),
    );
  }, [dispatch]);

  // Fetch company and contact data on initial load
  useEffect(() => {
    if (!hasInitializedFromUrl) return;

    if (currentStep === 'company-info-1') {
      dispatch(
        fetchLegalStructures({ api: clientApiV2, locale: selectedLocale }),
      );
    }
    if (currentStep === 'company-details') {
      dispatch(fetchIndustries({ api: clientApi, locale: selectedLocale }));
    }
    if (currentStep === 'company-details-npo') {
      dispatch(fetchIndustriesICNPO({ api: clientApiV2 }));
      dispatch(fetchNPOBeneficiaries({ api: clientApiV2 }));
      dispatch(fetchNPOStreams({ api: clientApiV2 }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, hasInitializedFromUrl]);

  useEffect(() => {
    dispatch(fetchCompany({ api: clientApi }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboarding]);

  //Fetch grants formdata
  useEffect(() => {
    if (!hasInitializedFromUrl) return;
    dispatch(
      fetchFundingExplorerTotalAmount({
        api: clientApi,
        ...fundingExplorerProps,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepData.formData, fundingExplorerProps]);

  // Derived state
  const isStepValid = useMemo(() => {
    // Directly use the currentStep and stepData from the Redux selectors
    return validateStep(currentStep, stepData, t, t2).isValid;
  }, [currentStep, stepData, t, t2]);

  //terms and conditions
  const isAcceptedTerms = useMemo(() => {
    return (
      contact?.terms_and_conditions_at !== undefined &&
      contact?.terms_and_conditions_at !== null
    );
  }, [contact]);

  const acceptTerms = useCallback((): Promise<Contact> => {
    return unwrap(
      acceptTermsAndConditions({
        api: clientApi,
      }),
    );
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Context value
  const contextValue: OnboardingContextType = {
    setStepData,
    skipOnboarding,
    stepData,
    currentStep,
    validateCurrentStep,
    handleSubmitStep,
    isStepValid,
    goToPreviousStep,
    goToStep,
    disableExpertEligible,
    openExpertLink,
    canSkipOnboarding: skipAvailable,
    onboardingForm,
    goToDashboard,
    flowSteps,
    stepsContent,
    currentStepContent,
    isExpertEligible: !!isExpertEligible,
    completedSteps,
    isAcceptedTerms,
    acceptTerms,
    goToFundingExplorer,
    goToCheckout,
    goToLink,
  };

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
    </OnboardingContext.Provider>
  );
};
